import {CircularProgress, createStyles, makeStyles, Theme, Typography} from "@material-ui/core";
import Colors from "Constants/Colors";
import { CarClass } from "Enums/CarClass";
import { FPSOArea } from "Models/FPSOArea";
import { FC, useCallback, useEffect, useState } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import { BasePieChart } from "Views/Components/FpsoOverview/Graphs/BasePieChart";
import {useAuth} from "../../../../auth";
import {CarUtils, RatioResult} from "../../../../Utils/CarUtils";

interface Props {
  readonly fpsoArea: FPSOArea;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    errorContainer: {
      padding: theme.spacing(2),
    },
  }),
);

export const CarClassRatioChart: FC<Props> = ({ fpsoArea }) => {
  const { t } = useTranslation("cars");
  const classes = useStyles();
  const { auth } = useAuth();

  const [chartData, setChartData] = useState<Plotly.Data[]>([]);
  const [data, setData] = useState<RatioResult[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const getData = useCallback(async () => {
    setLoading(true);
    const token = await auth?.getToken();
    const ratios = await CarUtils.GetRatio(
      "GetCarClassRatio",
      fpsoArea.id,
      `bearer ${token}`);

    setData(ratios?.data);
    setLoading(false);
  }, [fpsoArea.id, auth]);

  useEffect(() => {
    getData();
    // eslint-disable-next-line
  }, [fpsoArea.id]);

  const handleCarRatioDataReceived = useCallback(async () => {
    const keys: string[] = [];
    const values: number[] = [];
    const sectionColors: string[] = [];

    data.forEach((ratio) => {
      keys.push(
        ratio.result === CarClass.critical
          ? t("classCritical")
          : ratio.result === CarClass.major
            ? t("classMajor")
            : ratio.result === CarClass.minor
              ? t("classMinor")
              : ratio.result === CarClass.comment
                ? t("classComment")
                : "",
      );
      values.push(ratio.ratio);
      sectionColors.push(
        ratio.result === CarClass.critical
          ? Colors.CAR_CLASS_CRITICAL_PIE_CHART
          : ratio.result === CarClass.major
            ? Colors.CAR_CLASS_MAJOR_PIE_CHART
            : ratio.result === CarClass.minor
              ? Colors.CAR_CLASS_MINOR_PIE_CHART
              : ratio.result === CarClass.comment
                ? Colors.CAR_CLASS_COMMENT_PIE_CHART
                : "",
      );
    });

    const carClassTrace = {
      values,
      labels: keys,
      type: "pie",
      name: "Car class ratio",
      textinfo: "value",
      textfont: {
        family: '"Red Hat Display", sans-serif',
        size: 20,
        color: Colors.DARK_GREY,
      },
      showlegend: true,
      sort: false,
      marker: {
        colors: sectionColors,
        line: {
          color: Colors.WHITE,
          width: [4, 4, 4, 4],
        },
      },
    };

    const plotData: Plotly.Data[] = [];
    plotData.push(carClassTrace as Plotly.Data);
    setChartData(plotData);

    // eslint-disable-next-line
  }, [data]);

  useEffect(() => {
    handleCarRatioDataReceived();
    // eslint-disable-next-line
  }, [data]);

  const titletext = t("cars:carClassRatio");

  if (loading) {
    return <CircularProgress />;
  }

  if (data.length !== 0) {
    return <BasePieChart data={chartData} titleText={titletext} />;
  }

  if (data.length === 0) {
    return (
      <div className={classes.errorContainer}>
        <Typography>{t("errors:carRatioChartFetchNoData")}</Typography>
      </div>
    );
  }

  return (
    <div className={classes.errorContainer}>
      <Typography>{t("errors:carRatioChartFetchError")}</Typography>
    </div>
  );
};
